import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, FileText, Receipt, Mail, LogOut, Download } from 'lucide-react';
import api from '@/lib/api';

function PortalSidebar({ activeRoute }) {
  const { user, logout } = useAuth();

  const navItems = [
    { path: '/portal', label: 'Overview', exact: true },
    { path: '/portal/insights', label: 'Latest Insights', icon: FileText },
    { path: '/portal/bills', label: 'Bills & Invoices', icon: Receipt },
    { path: '/portal/contact', label: 'Contact Admin', icon: Mail },
  ];

  return (
    <aside className="w-64 bg-[var(--navy-900)] text-[var(--cream)] min-h-[calc(100vh-4rem)] flex flex-col">
      <nav className="flex-1 py-4">
        {navItems.map((item) => {
          const isActive = item.exact
            ? activeRoute === item.path
            : activeRoute.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
            >
              {item.icon && <item.icon className="h-5 w-5" />}
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="px-4 py-2 text-sm">
          <p className="font-medium">{user?.name}</p>
          <p className="text-[var(--cream)]/60 text-xs truncate">{user?.email}</p>
        </div>
        <button
          onClick={logout}
          className="sidebar-link w-full text-left mt-2"
        >
          <LogOut className="h-5 w-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

function PortalOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-2">
        Welcome, {user?.name?.split(' ')[0]}
      </h1>
      <p className="text-[var(--muted-foreground)] mb-8">
        Access your research reports and account documents.
      </p>
      
      <div className="space-y-4">
        <button
          onClick={() => navigate('/portal/insights')}
          className="btn-big w-full text-left flex items-center gap-4"
        >
          <FileText className="h-6 w-6" />
          <div>
            <span className="font-semibold">Latest Insights</span>
            <p className="text-sm text-[var(--muted-foreground)] font-normal">Street analysis and tactical insights</p>
          </div>
        </button>
        <button
          onClick={() => navigate('/portal/bills')}
          className="btn-big w-full text-left flex items-center gap-4"
        >
          <Receipt className="h-6 w-6" />
          <div>
            <span className="font-semibold">Bills & Invoices</span>
            <p className="text-sm text-[var(--muted-foreground)] font-normal">View and download billing documents</p>
          </div>
        </button>
        <button
          onClick={() => navigate('/portal/contact')}
          className="btn-big w-full text-left flex items-center gap-4"
        >
          <Mail className="h-6 w-6" />
          <div>
            <span className="font-semibold">Contact Admin</span>
            <p className="text-sm text-[var(--muted-foreground)] font-normal">Get in touch with our team</p>
          </div>
        </button>
      </div>
    </div>
  );
}

function DocumentsList({ type, title, emptyMessage }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [type]);

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/my/documents?type=${type}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloading(doc.document_id);
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || 'document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--navy-800)]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-8">{title}</h1>
      
      {documents.length === 0 ? (
        <div className="card-newspaper text-center py-12">
          <FileText className="h-12 w-12 text-[var(--muted-foreground)]/30 mx-auto mb-4" />
          <p className="text-[var(--muted-foreground)]">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.document_id} className="card-newspaper flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileText className="h-5 w-5 text-[var(--navy-800)]" />
                <div>
                  <h3 className="font-medium text-[var(--navy-800)]">{doc.title}</h3>
                  <p className="text-sm text-[var(--muted-foreground)]">
                    {new Date(doc.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDownload(doc)}
                className="btn-big py-2 px-4 text-sm flex items-center gap-2"
                disabled={downloading === doc.document_id}
              >
                {downloading === doc.document_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PortalContact() {
  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-3xl text-[var(--navy-800)] mb-8">Contact Admin</h1>
      
      <div className="card-newspaper">
        <p className="text-[var(--muted-foreground)] mb-6">
          Have questions about your account or need assistance? Reach out to us.
        </p>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-[var(--navy-800)] mb-1">New York Office</h3>
            <a href="mailto:nyc@coastwisetrade.com" className="text-[var(--navy-800)] underline">
              nyc@coastwisetrade.com
            </a>
          </div>
          <div>
            <h3 className="font-semibold text-[var(--navy-800)] mb-1">Chicago Office</h3>
            <a href="mailto:chicago@coastwisetrade.com" className="text-[var(--navy-800)] underline">
              chicago@coastwisetrade.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--navy-800)]" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="client-portal-page">
      {/* Header */}
      <header className="h-16 bg-[var(--navy-800)] flex items-center justify-between px-6">
        <Link to="/" className="font-serif text-xl text-[var(--cream)]">
          Coastwise Trade
        </Link>
        <span className="text-sm text-[var(--cream)]/60">Client Portal</span>
      </header>

      <div className="flex">
        <PortalSidebar activeRoute={location.pathname} />
        <main className="flex-1 p-8">
          <Routes>
            <Route index element={<PortalOverview />} />
            <Route
              path="insights"
              element={
                <DocumentsList
                  type="insight"
                  title="Latest Insights"
                  emptyMessage="No insights available yet."
                />
              }
            />
            <Route
              path="bills"
              element={
                <DocumentsList
                  type="bill"
                  title="Bills & Invoices"
                  emptyMessage="No billing documents available."
                />
              }
            />
            <Route path="contact" element={<PortalContact />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
